import * as turf from "@turf/turf";
import L from "leaflet";
import { clearLayer } from "helpers/map";
import { splitPixelPolygonByLine } from "modules/aiMapping/aiMappingGeometry";

type GeoPoint = [number, number];

export interface MeasuredAreaOpsContext {
  map: L.Map;
  featureGroup: { current: L.FeatureGroup | null };
  isTouch: boolean;
  labelScale: number;
  defaultColor: () => string;
  addLayerEvents: (layer: L.Layer) => void;
  addLayerLabel: (layer: L.Layer, isTouch: boolean) => void;
  addLayerLineLengths: (map: L.Map, layer: L.Layer, labelScale: number) => void;
  onEdited: () => void;
}

const toTurfPolygon = (points: GeoPoint[]): any => {
  const ring = points.map(([lat, lng]) => [lng, lat] as [number, number]);
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) ring.push(first);
  return turf.polygon([ring] as any);
};

const isManualAreaLayer = (layer: L.Layer): layer is L.Polygon =>
  layer instanceof L.Polygon && (layer.feature?.properties as any)?.aiManaged !== true;

const polygonGeoOf = (layer: L.Layer): any | null => {
  try {
    const geo = (layer as L.Polygon).toGeoJSON();
    return geo.geometry.type === "Polygon" ? geo : null;
  } catch {
    return null;
  }
};

const piecesByArea = (diff: any): number[][] => {
  const polygonRings =
    diff.geometry.type === "Polygon" ? [diff.geometry.coordinates] : diff.geometry.coordinates;
  return polygonRings
    .map((rings: any) => rings[0] as number[][])
    .filter((outer: any) => outer?.length >= 4)
    .map((outer: number[][]) => outer.slice(0, -1).flatMap(([lng, lat]) => [lat, lng]))
    .map((piece) => ({ piece, area: ringArea(piece) }))
    .sort((a, b) => b.area - a.area)
    .map(({ piece }) => piece);
};

const ringArea = (flat: number[]): number => {
  const points: [number, number][] = [];
  for (let i = 0; i + 1 < flat.length; i += 2) points.push([flat[i], flat[i + 1]]);
  let area = 0;
  for (let i = 0; i < points.length; i += 1) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];
    area += x1 * y2 - x2 * y1;
  }
  return Math.abs(area) / 2;
};

const removeLayer = (ctx: MeasuredAreaOpsContext, layer: L.Layer): void => {
  clearLayer(ctx.map, layer);
  ctx.featureGroup.current?.removeLayer(layer);
  layer.remove();
};

const replaceWithPieces = (
  ctx: MeasuredAreaOpsContext,
  target: L.Polygon,
  pieces: number[][]
): void => {
  const color = (target.feature?.properties as any)?.color || ctx.defaultColor() || "#f44336";
  const baseProperties = { ...((target.feature?.properties as any) ?? {}), showTooltip: false };
  const featureId = String((target.feature?.properties as any)?.featureId ?? "");
  const toLayer = (flatRing: number[], id: string): L.Polygon => {
    const ring: [number, number][] = [];
    for (let index = 0; index + 1 < flatRing.length; index += 2) {
      ring.push([flatRing[index], flatRing[index + 1]]);
    }
    const polygonLayer = L.polygon(
      ring.map(([lat, lng]) => L.latLng(lat, lng)),
      { color, weight: 3, fillColor: color, fillOpacity: 0.3 }
    );
    polygonLayer.feature = {
      type: "Feature",
      properties: { ...baseProperties, featureId: id },
      geometry: { type: "Polygon", coordinates: [ring.map(([lat, lng]) => [lng, lat])] },
    } as any;
    return polygonLayer;
  };

  const uniqueFeatureId = (): string => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    return `piece-${Date.now()}-${Math.round(Math.random() * 1000000)}`;
  };
  const newLayers = pieces.map((piece, index) =>
    toLayer(piece, index === 0 ? featureId : uniqueFeatureId())
  );
  clearLayer(ctx.map, target);
  ctx.featureGroup.current?.removeLayer(target);
  target.remove();
  for (const newLayer of newLayers) {
    ctx.featureGroup.current?.addLayer(newLayer);
    ctx.addLayerEvents(newLayer);
    ctx.addLayerLineLengths(ctx.map, newLayer, ctx.labelScale);
    ctx.addLayerLabel(newLayer, ctx.isTouch);
  }
};

export function splitManualAreasAlongLine(ctx: MeasuredAreaOpsContext, pts: GeoPoint[]): number {
  if (pts.length < 2) return 0;
  const group = ctx.featureGroup.current;
  if (!group) return 0;
  const a = pts[0];
  const b = pts[pts.length - 1];
  const targets: { layer: L.Polygon; pieces: number[][] }[] = [];
  group.eachLayer((existing: any) => {
    if (!isManualAreaLayer(existing)) return;
    const geo = polygonGeoOf(existing);
    if (!geo) return;
    const flat = (geo.geometry.coordinates[0] as [number, number][]).flatMap(([lng, lat]) => [
      lat,
      lng,
    ]);
    const pieces = splitPixelPolygonByLine(flat, a, b);
    if (!pieces) return;
    targets.push({ layer: existing, pieces: [pieces[0], pieces[1]] });
  });
  for (const { layer, pieces } of targets) replaceWithPieces(ctx, layer, pieces);
  if (targets.length) ctx.onEdited();
  return targets.length;
}

export function eraseManualAreasWithPolygon(
  ctx: MeasuredAreaOpsContext,
  points: GeoPoint[]
): number {
  if (points.length < 3) return 0;
  const group = ctx.featureGroup.current;
  if (!group) return 0;
  let eraser: any;
  try {
    eraser = toTurfPolygon(points);
  } catch {
    return 0;
  }
  let affected = 0;
  for (const layer of group.getLayers()) {
    if (!isManualAreaLayer(layer)) continue;
    const geo = polygonGeoOf(layer);
    if (!geo) continue;
    try {
      if (!turf.intersect(geo as any, eraser)) continue;
      const diff = turf.difference(geo as any, eraser);
      const pieces = diff ? piecesByArea(diff) : [];
      if (!pieces.length) {
        removeLayer(ctx, layer);
      } else {
        replaceWithPieces(ctx, layer, pieces);
      }
      affected += 1;
    } catch {
      /* ignore non-parsable layers */
    }
  }
  if (affected) ctx.onEdited();
  return affected;
}

export function mergeManualAreasWithPolygon(
  ctx: MeasuredAreaOpsContext,
  points: GeoPoint[]
): number {
  if (points.length < 3) return 0;
  const group = ctx.featureGroup.current;
  if (!group) return 0;
  let mergePolygon: any;
  try {
    mergePolygon = toTurfPolygon(points);
  } catch {
    return 0;
  }
  const touching: L.Polygon[] = [];
  group.eachLayer((existing: any) => {
    if (!isManualAreaLayer(existing)) return;
    const geo = polygonGeoOf(existing);
    if (!geo) return;
    try {
      const union = turf.union(mergePolygon, geo as any);
      if (union && union.geometry.type === "Polygon") touching.push(existing);
    } catch {
      /* ignore non-parsable layers */
    }
  });
  if (!touching.length) return 0;

  let unionGeo: any = mergePolygon;
  try {
    for (const target of touching) {
      unionGeo = turf.union(unionGeo, target.toGeoJSON() as any);
    }
  } catch {
    return 0;
  }
  if (!unionGeo || unionGeo.geometry.type !== "Polygon") return 0;

  const primary = touching.reduce((a, b) =>
    turf.area(b.toGeoJSON() as any) > turf.area(a.toGeoJSON() as any) ? a : b
  );
  primary.setLatLngs(
    (unionGeo.geometry.coordinates[0] as [number, number][]).map(([lng, lat]) => L.latLng(lat, lng))
  );
  if (primary.feature) {
    primary.feature = { ...primary.feature, geometry: unionGeo.geometry } as any;
  }
  for (const absorbed of touching) {
    if (absorbed !== primary) {
      clearLayer(ctx.map, absorbed);
      group.removeLayer(absorbed);
      absorbed.remove();
    }
  }
  ctx.onEdited();
  return touching.length;
}
